import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ActionNeeded } from '@/components/ActionNeeded';
import { AnnouncementTicker } from '@/components/AnnouncementTicker';
import { ErrorNote } from '@/components/ui';
import { formatDate } from '@/format';
import { openSettingsPage } from '@/screens/settings/pages';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { usePreferences } from '@/stores/preferences';
import { useShellNav } from '@/stores/shellNav';
import { AnnouncementBoard } from './AnnouncementBoard';
import { ComingUp } from './ComingUp';
import { HeadlineCards } from './HeadlineCards';
import { arrangeWidgets } from './layout';
import { REFRESH_MS, useOverview } from './useOverview';

/**
 * The home dashboard: the figures worth a glance, the announcements put up for
 * this person, and everything waiting for a decision.
 *
 * Built from a registry (`widgets.ts`) rather than a fixed page, for two
 * reasons: each person chooses in Settings › Dashboard what they want and in
 * what order, and a module built later adds its widget to the registry without
 * touching this file.
 */

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function Dashboard() {
  const { user, can } = useAuth();
  const navigate = useShellNav((s) => s.navigate);
  const layout = usePreferences((s) => s.prefs.dashboardLayout);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const { data: overview, isPending, isError, error } = useOverview();

  // The count on the "Waiting for you" card, and the list further down, are the
  // same request: two numbers that disagree would be worse than one.
  const actionItems = useQuery({
    queryKey: ['dashboard', 'action-items', farmIds],
    queryFn: () => api.get('/api/v1/dashboard/action-items', farmScopeQuery(farmIds)),
    refetchInterval: REFRESH_MS,
  });

  const today = useQuery({
    queryKey: ['calendar', 'today'],
    queryFn: () => api.get('/api/v1/calendar/today'),
  });

  const sent = useMemo(() => {
    const sections = overview?.sections;
    return (needs?: string) => (needs === undefined ? true : Boolean(sections?.[needs as never]));
  }, [overview]);

  const cards = useMemo(
    () => arrangeWidgets('card', layout, can, (w) => sent(w.needs)),
    [layout, can, sent],
  );
  const sections = useMemo(
    () => arrangeWidgets('section', layout, can, (w) => sent(w.needs)),
    [layout, can, sent],
  );

  if (!user) return null;

  const waiting = (actionItems.data ?? []).reduce((sum, item) => sum + item.count, 0);
  const farmName = overview?.farmName ?? null;
  const holidayToday = (today.data ?? [])[0];

  const panel = (id: string): ReactNode => {
    switch (id) {
      case 'action-needed':
        return (
          <div id="dashboard-action-needed" key={id}>
            <ActionNeeded farmIds={farmIds} />
          </div>
        );
      case 'announcements':
        return <AnnouncementBoard key={id} />;
      case 'coming-up':
        return <ComingUp key={id} onOpenCalendar={() => navigate?.('home-calendar')} />;
      default:
        return null;
    }
  };

  const main = sections.shown.filter((s) => s.column === 'main');
  const side = sections.shown.filter((s) => s.column !== 'main');

  return (
    <div className="dashboard">
      <AnnouncementTicker />

      <header className="dashhead">
        <div>
          <h2 className="dashhead__title">
            {greeting(new Date().getHours())}, {user.fullName}
          </h2>
          <p className="dashhead__sub">
            {formatDate(new Date())}
            {holidayToday ? ` · ${holidayToday.nameEn}` : ''} ·{' '}
            {farmName ?? (user.scope.allFarms ? 'All farms' : `${user.scope.farmIds.length} farm(s)`)}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={() => openSettingsPage('dashboard', (id) => navigate?.(id))}
        >
          Change what shows
        </button>
      </header>

      {isError && <ErrorNote message={error.message} />}
      {isPending && <p className="muted">Loading your figures…</p>}

      {overview && (
        <HeadlineCards
          cards={cards.shown}
          overview={overview}
          waiting={waiting}
          onNavigate={(id, intent) => navigate?.(id, intent)}
        />
      )}

      {sections.shown.length > 0 && (
        // With one column switched off, the other takes the whole width
        // rather than leaving a gap where it was.
        <div
          className={`dashboard__body${main.length === 0 || side.length === 0 ? ' dashboard__body--single' : ''}`}
        >
          {main.length > 0 && <div className="dashboard__main">{main.map((s) => panel(s.id))}</div>}
          {side.length > 0 && <div className="dashboard__side">{side.map((s) => panel(s.id))}</div>}
        </div>
      )}

      {cards.shown.length === 0 && sections.shown.length === 0 && (
        <p className="muted">
          Everything is switched off. Use “Change what shows” to bring something back.
        </p>
      )}
    </div>
  );
}
